import { HdTranslateFn, hdAuthorityLabel, hdDefinitionLabel, hdTypeLabel } from './hd-chart-property-labels';

/** Optie voor de eigenschappen-filterbalk (canonieke key + vertaald label + optioneel icoon). */
export interface HdPropertyFilterOption {
  value: string;
  label: string;
  /** Kortere tekst voor de gekozen chip (bv. enkel het poortnummer); standaard het label. */
  chipLabel?: string;
  icon?: string;
  /** Gedempt (niet selecteerbaar) wanneer de facet-count 0 is; ng-select leest dit veld standaard. */
  disabled?: boolean;
}

/** FA7-iconen per energietype (voorstel fase 3, CHART_MANAGEMENT_PLAN.md). */
export const HD_ENERGY_TYPE_ICONS: Readonly<Record<string, string>> = {
  generator: 'bolt',
  manifestingGenerator: 'bolt-lightning',
  manifestor: 'rocket',
  projector: 'eye',
  reflector: 'moon',
};

const HD_TYPE_KEYS = ['generator', 'manifestingGenerator', 'manifestor', 'projector', 'reflector'] as const;

const HD_AUTHORITY_KEYS = [
  'emotional', 'sacral', 'splenic', 'ego', 'selfProjected', 'externalEnvironmental', 'lunar',
] as const;

const HD_DEFINITION_KEYS = ['none', 'single', 'split', 'tripleSplit', 'quadrupleSplit'] as const;

/** De 12 bestaande profielen, in de klassieke volgorde. */
const HD_PROFILE_KEYS = [
  '1/3', '1/4', '2/4', '2/5', '3/5', '3/6', '4/6', '4/1', '5/1', '5/2', '6/2', '6/3',
] as const;

export function buildHdTypeFilterOptions(translate: HdTranslateFn): HdPropertyFilterOption[] {
  return HD_TYPE_KEYS.map(value => ({
    value,
    label: hdTypeLabel(translate, value) ?? value,
    icon: HD_ENERGY_TYPE_ICONS[value],
  }));
}

export function buildHdAuthorityFilterOptions(translate: HdTranslateFn): HdPropertyFilterOption[] {
  return HD_AUTHORITY_KEYS.map(value => ({ value, label: hdAuthorityLabel(translate, value) ?? value }));
}

export function buildHdDefinitionFilterOptions(translate: HdTranslateFn): HdPropertyFilterOption[] {
  return HD_DEFINITION_KEYS.map(value => ({ value, label: hdDefinitionLabel(translate, value) ?? value }));
}

export function buildHdProfileFilterOptions(): HdPropertyFilterOption[] {
  return HD_PROFILE_KEYS.map(value => ({ value, label: value }));
}

/** De 64 poorten voor het poortfilter: "13 · keynote" in de lijst, enkel "13" als chip. */
export function buildHdGateFilterOptions(keynote: (gate: number) => string | null | undefined): HdPropertyFilterOption[] {
  return Array.from({ length: 64 }, (_, index) => {
    const gate = index + 1;
    const name = keynote(gate)?.trim();
    return { value: `${gate}`, label: name ? `${gate} · ${name}` : `${gate}`, chipLabel: `${gate}` };
  });
}

/**
 * Zoekfunctie van het poortfilter: een getal treft exact dat poortnummer (anders
 * lichten bij "1" ook 10 t/m 19, 21, 31 … op), tekst zoekt in de keynote.
 */
export function hdGateFilterSearch(term: string, option: HdPropertyFilterOption): boolean {
  const query = term.trim().toLowerCase();
  if (/^\d+$/.test(query)) return option.value === `${Number(query)}`;
  return option.label.toLowerCase().includes(query);
}

/**
 * Verrijkt opties met facet-counts: "Label (n)"; count 0 → gedempt (disabled)
 * maar zichtbaar. Zonder facetten (nog niet geladen) blijven de opties kaal.
 */
export function withFacetCounts(
  options: HdPropertyFilterOption[],
  facets: { key?: string | null; count?: number }[] | null | undefined,
): HdPropertyFilterOption[] {
  if (!facets) return options;
  const countByKey = new Map(facets.map(facet => [facet.key ?? '', facet.count ?? 0]));
  return options.map(option => {
    const count = countByKey.get(option.value) ?? 0;
    return { ...option, label: `${option.label} (${count})`, disabled: count === 0 };
  });
}
